import { StudentProfileModel } from "app/models/scheduling/StudentProfileModel";

/**
 * The family members to badge on a session card (CN-1323, CFTB-3336-followup).
 * Show nothing unless the session actually includes a managed dependent; then
 * name everyone in the family who attends — the head too, when they're a
 * participant alongside a child — so a "me + child" session reads differently
 * from a "child only" one. A head-only session has no dependents, so it stays
 * badge-less.
 *
 * The head is the participant whose `id` matches the dependents' headExternalId
 * (a profile's `id` is its external id here, mirroring soleFamilyHead in
 * familyPayer). The head is listed first, then the dependents.
 */
export const sessionBadgeParticipants = (session: {
  studentProfiles?: StudentProfileModel[];
}): StudentProfileModel[] => {
  const profiles = session?.studentProfiles ?? [];
  const dependents = profiles.filter((sp) => sp?.headExternalId);
  if (dependents.length === 0) {
    return [];
  }

  const headExternalId = dependents[0].headExternalId;
  const head = profiles.find((sp) => sp?.id === headExternalId);
  return head ? [head, ...dependents] : dependents;
};

/**
 * Full name from first/last fields rather than the model's `fullName` getter:
 * sessions reached through the (redux-persisted) edit flow are plain objects
 * without the getter, so we derive the name from data that always survives.
 */
export const participantFullName = (
  participant: Pick<StudentProfileModel, "firstName" | "lastName">,
): string =>
  `${participant.firstName ?? ""} ${participant.lastName ?? ""}`.trim();
